import { AIAssistantProvider } from "@jugl-web/domain-resources/ai-assistant/providers";
import { OverlayQueueProvider } from "@jugl-web/domain-resources/common/providers/OverlayQueueProvider";
import { liveUpdateEvents$ } from "@jugl-web/domain-resources/live-updates";
import { ActiveTimeTrackingProvider } from "@jugl-web/domain-resources/task-time-tracking/providers/ActiveTimeTrackingProvider";
import { TaskLiveUpdatesProvider } from "@jugl-web/domain-resources/tasks/components/TaskLiveUpdatesProvider";
import {
  cx,
  useAppVariant,
  usePrevious,
  useTabIsActive,
} from "@jugl-web/utils";
import { CXHomeSidebar } from "@web-src/modules/cx/components/CXHomeSidebar";
import { EntityModulesChangesDialog } from "@web-src/modules/entities/components/EntityModulesChangesDialog";
import IncomingCallBanner from "@web-src/modules/conference/components/IncomingCallBanner";
import { LiveUpdatesListener } from "@web-src/modules/socket/components/LiveUpdatesListener";
import { EntityLiveUpdatesListener } from "@web-src/modules/socket/components/EntityLiveUpdatesListener";
import { UnreadIndicatorsProvider } from "@web-src/modules/common/providers/UnreadIndicatorsProvider";
import { CRMUnreadIndicatorsProvider } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { environment } from "@web-src/environments/environment";
import { HomeSidebar } from "@web-src/modules/navigation/components/HomeSidebar";
import { MainNavBar } from "@web-src/modules/navigation/components/MainNavBar";
import { TabRoute } from "@web-src/modules/navigation/types";
import { useUpdateTokenMutation } from "@web-src/modules/auth/authApi";
import {
  selectAuthState,
  selectUserId,
} from "@web-src/modules/auth/store/authSlice";
import { useNavigateToTaskDetailsPage } from "@web-src/modules/tasks/hooks/useNavigateToTaskDetailsPage";
import ActiveCallWrapper from "@web-src/modules/conference/pages/ConferencePage/components/ActiveCallWrapper";
import { WelcomeToEntityAlert } from "@web-src/modules/entities/components/WelcomeToEntityAlert";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { AppRoutes } from "@web-src/modules/navigation/components/AppRoutes";
import { useCurrentPageNavConfig } from "@web-src/modules/navigation/hooks/useCurrentPageNavConfig";
import { useFCM } from "@web-src/modules/notifications/providers/FCMProvider/useFCM";
import { usePhoenixSocket } from "@web-src/modules/socket/providers/usePhoenixSocket";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "react-router-dom";
import { useNetworkState } from "react-use";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { KnowledgeTrainingProvider } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";
import { SidebarNavProvider } from "@web-src/modules/navigation/providers/SidebarNavProvider";

const App: React.FC = () => {
  const { isAuthenticated } = useSelector(selectAuthState);
  const meId = useSelector(selectUserId);
  const { isAnonymous } = useAnonymousSession();
  const navigateToTaskDetailsPage = useNavigateToTaskDetailsPage();
  const { isCx } = useAppVariant();
  const currentPageNavConfig = useCurrentPageNavConfig();
  const { subscriptionInfo, entity } = useEntityProvider();

  const [updateToken] = useUpdateTokenMutation();
  const { token } = useFCM();

  useEffect(() => {
    if (!token || !isAuthenticated) {
      return;
    }
    updateToken(token);
  }, [updateToken, token, isAuthenticated]);

  const { online } = useNetworkState();

  useEffect(() => {
    // make sure app will be reloaded after user wakes up
    const wakeupWorker = new Worker("/detect-wakeup.js");
    wakeupWorker.onmessage = (ev) => {
      if (ev?.data === "app:wakeup") {
        window.location.reload();
      }
    };
  }, []);

  const { channel, socket } = usePhoenixSocket();
  const tabIsActive = useTabIsActive();
  const previousTabIsActive = usePrevious(tabIsActive);

  const isNavigationVisible = !currentPageNavConfig?.isNavigationHidden;

  useEffect(() => {
    if (tabIsActive === previousTabIsActive) {
      return;
    }
    channel?.push(
      "device_status",
      tabIsActive ? { status: null } : { status: "offline" }
    );
  }, [channel, tabIsActive, previousTabIsActive]);

  const $authenticatedContent = useMemo(() => {
    let $content: React.ReactNode = null;

    if (environment.isDemo) {
      $content = (
        <div className="flex h-screen w-screen flex-col">
          <AppRoutes />
        </div>
      );
    } else if (entity && (subscriptionInfo?.planIsActive || isAnonymous)) {
      $content = (
        <>
          <EntityModulesChangesDialog />
          <ActiveCallWrapper />
          <div className="fixed top-0 left-0 flex h-[100dvh] w-[100vw] min-w-0 flex-col">
            {isNavigationVisible && <MainNavBar hasSidebarNav />}
            <div
              className={cx(
                "flex min-h-0 min-w-0",
                isNavigationVisible && "h-[calc(100%-_48px)]"
              )}
            >
              {isCx ? <CXHomeSidebar /> : <HomeSidebar />}
              <div className="z-10 h-full w-full min-w-0 grow overflow-y-auto">
                <AppRoutes />
              </div>
            </div>
          </div>
          <WelcomeToEntityAlert />
        </>
      );
    } else {
      $content = <AppRoutes />;
    }

    if (entity) {
      const contentWithProviders = (
        <KnowledgeTrainingProvider entityId={entity.id}>
          <OverlayQueueProvider>
            <ActiveTimeTrackingProvider
              entityId={entity.id}
              isDisabled={isAnonymous || entity.type === "cx"}
              onNavigateToTask={(taskId, taskBoardId) =>
                navigateToTaskDetailsPage({
                  taskId,
                  customTasksSource: taskBoardId
                    ? { type: "boardTasks", boardId: taskBoardId }
                    : undefined,
                })
              }
            >
              {$content}
            </ActiveTimeTrackingProvider>
          </OverlayQueueProvider>
        </KnowledgeTrainingProvider>
      );

      $content = isAnonymous ? (
        contentWithProviders
      ) : (
        <AIAssistantProvider socket={socket} userId={meId} entityId={entity.id}>
          {contentWithProviders}
        </AIAssistantProvider>
      );
    }
    return $content;
  }, [
    entity,
    subscriptionInfo?.planIsActive,
    isAnonymous,
    isNavigationVisible,
    isCx,
    socket,
    meId,
    navigateToTaskDetailsPage,
  ]);

  const taskDetailsPageMatch = useMatch(
    `/:entityId/${TabRoute.tasks}/details/:taskId`
  );

  const isInTaskDetailsView = !!taskDetailsPageMatch;

  return (
    <>
      {!online && (
        <div className="fixed top-10 left-[50%] z-[2100] -translate-x-[50%] rounded bg-tertiary-500 px-12 py-5 text-center font-bold text-white">
          No internet connection, please reconnect to use Jugl
        </div>
      )}
      <div className={cx({ "opacity-50": !online })}>
        <SidebarNavProvider>
          {isAuthenticated || isAnonymous ? (
            <>
              <IncomingCallBanner />
              <LiveUpdatesListener events$={liveUpdateEvents$} />
              <EntityLiveUpdatesListener />
              <TaskLiveUpdatesProvider
                events$={liveUpdateEvents$}
                meId={meId}
                isInTaskDetailsView={isInTaskDetailsView}
              >
                <UnreadIndicatorsProvider>
                  <CRMUnreadIndicatorsProvider>
                    {$authenticatedContent}
                  </CRMUnreadIndicatorsProvider>
                </UnreadIndicatorsProvider>
              </TaskLiveUpdatesProvider>
            </>
          ) : (
            <AppRoutes />
          )}
        </SidebarNavProvider>
      </div>
    </>
  );
};

export default App;
